import React, { useEffect, useState } from 'react';
import Button from '@components/forms/Button';
import Input from '@components/forms/Input';
import Modal from './Modal';
import { BiLoaderAlt } from 'react-icons/bi';

type Props = {
  open: boolean;
  currentEmail: string;
  onClose: () => void;
  onConfirm: (email: string) => void;
  isLoading: boolean;
  showSuccessMessage: string;
  showErrorMessage: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EditSellerEmailModal: React.FC<Props> = ({
  open,
  currentEmail,
  onClose,
  onConfirm,
  isLoading,
  showSuccessMessage,
  showErrorMessage
}) => {
  const [email, setEmail] = useState(currentEmail);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    setEmail(currentEmail);
    setValidationError('');
  }, [currentEmail, open]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (validationError) setValidationError('');
  };

  const handleConfirm = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setValidationError('Email address is required.');
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setValidationError('Please enter a valid email address.');
      return;
    }
    if (trimmed === currentEmail) {
      setValidationError('The new email is the same as the current email.');
      return;
    }
    setValidationError('');
    onConfirm(trimmed);
  };

  return (
    <Modal title="Update Seller Email" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-[10px]">
        <div className="flex flex-col gap-2 text-[#475467]">
          <p>
            You can update the seller&apos;s email address below. This is only
            available for newly created inspections before the seller has
            uploaded any files.
          </p>
        </div>
        <div className="mt-4">
          <p className="text-sm font-medium text-grey-700 mb-1.5">
            Email Address
          </p>
          <Input
            placeholder="seller@example.com"
            type="email"
            value={email}
            onChange={handleEmailChange}
          />
          {validationError && (
            <p className="text-error-500 text-sm mt-1">{validationError}</p>
          )}
        </div>

        <div className="mt-2">
          {showSuccessMessage && showSuccessMessage !== '' && (
            <div className="h-4 w-full text-right mb-4">
              <div className="text-green-500">{showSuccessMessage}</div>
            </div>
          )}
          {showErrorMessage && showErrorMessage !== '' && (
            <div className="w-full text-center mb-4">
              <div className="text-error-500">{showErrorMessage}</div>
            </div>
          )}
          <div className="flex mt-4 gap-4 justify-center lg:justify-end">
            <Button
              variant="outlined"
              data-dialog-close="true"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-600 normal-case font-semibold rounded-lg text-base w-auto focus:ring focus:ring-white">
              Cancel
            </Button>
            <Button
              data-dialog-close="true"
              disabled={isLoading}
              className="font-semibold bg-primary normal-case rounded-lg text-base text-white w-auto whitespace-nowrap"
              onClick={handleConfirm}>
              {isLoading ? (
                <BiLoaderAlt className="animate-spin mr-1" />
              ) : null}
              Update Email
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditSellerEmailModal;
